import * as SR from '@microsoft/signalr'
import { useToast } from 'vue-toastification'
import { i18nInstance } from './localization'
import store from '~/store'
import { fetchRefreshToken } from './nikan-api'

if (!SR)
  throw new Error('[Vue-SignalR] Cannot locate signalr-client')

import { EventEmitter } from 'eventemitter3'

const defaultOptions = {
  log: false,
}

class SocketConnection extends EventEmitter {
  constructor(connection) {
    super()

    this.connection = connection
    this.listened = []
    this.socket = false

    this.toSend = []

    this.offline = false
    this._isMounted = false

    this.toast = {
      displayed: false,
      instance: null,
      options: {
        id: 'socket_toast',
        position: 'bottom-center',
        type: 'info',
        rtl: true,
        timeout: false,
        closeOnClick: false,
        closeButton: false,
        pauseOnHover: true,
        showCloseButtonOnHover: false,
        // container: asyncGetContainer,
        newestOnTop: true,
        maxToasts: 1,
        transition: 'Vue-Toastification__fade',
        draggable: false,
        pauseOnFocusLoss: true,
        hideProgressBar: false,
        toastClassName: [],
        bodyClassName: [],
        onMounted: undefined,
      },
    }

    this.toast.instance = useToast(this.toast.options)
  }
  listenedItems() {
    return this.listened
  }

  async _initialize(connection = '') {
    const con = connection || this.connection

    try {
      const socket = new SR.HubConnectionBuilder()
        .withUrl(con)
        .withAutomaticReconnect()
        .build()

      socket.connection.onclose = async (error) => {
        if (this._isMounted) {
          if (this.options.log) console.log('Reconnecting...')

          this.displayErrorToast()
          this.socket = false
          store.dispatch('formCodes/clearFormCodes')

          /* eslint-disable no-underscore-dangle */
          await this._initialize(con)
          this.emit('reconnect')
        }
      }

      await socket.start()

      this.dismissErrorToast()
      this.socket = socket
      this._isMounted = true
      store.dispatch('formCodes/clearFormCodes')

      this.emit('init')
    } catch (error) {

      if (error?.message?.includes("'401'")) {
        const rs = await fetchRefreshToken()

        store.dispatch('useraccount/applyDetails', { user: rs })

        this.authenticate(rs.access_token, rs.fiscalPeriodId, rs.userId, rs.companyId, rs.customerCode)

        return
      }

      if (this.options.log) console.log('Error, reconnecting...')

      this.displayErrorToast()

      setTimeout(() => {
        this._initialize(con)
      }, 1000)
    }
  }

  async start(options = {}) {
    this.options = Object.assign(defaultOptions, options)

    await this._initialize()
  }

  async stop() {
    this._isMounted = false

    await this.socket.stop()
  }

  async authenticate(accessToken, fiscalPeriodId, userId, companyId, customerCode, options = {}) {
    this.connection = `${this.connection.split('?')[0]}?access_token=${accessToken}`

    /* eslint-disable no-underscore-dangle */
    await this.start(options)
  }

  listen(method) {
    if (this.offline) return

    if (this.listened.some(v => v === method)) return
    this.listened.push(method)

    this.on('init', () => {
      this.socket.on(method, (data) => {
        if (this.options.log) console.log({ type: 'receive', method, data })

        this.emit(method, data)
      })
    })
  }

  send(methodName, ...args) {
    if (this.options.log) console.log({ type: 'send', methodName, args })
    if (this.offline) return

    if (this.socket) {
      this.socket.send(methodName, ...args)
      return
    }

    this.once('init', () => this.socket.send(methodName, ...args))
  }

  async invoke(methodName, ...args) {
    if (this.options.log) console.log({ type: 'invoke', methodName, args })
    if (this.offline) return false

    if (this.socket) {
      return this.socket.invoke(methodName, ...args)
    }

    return new Promise(async resolve =>
      this.once('init', () =>
        resolve(this.socket.invoke(methodName, ...args))))
  }

  displayErrorToast() {
    if (!this.toast.displayed) {
      this.toast.instance.error(i18nInstance.global.t('connection-lost'), this.toast.options)
      this.toast.displayed = true
    }
  }

  dismissErrorToast() {
    if (this.toast.displayed) {
      this.toast.instance.update(this.toast.options.id, { options: { type: 'success', timeout: 1000 }, content: i18nInstance.global.t('connected') })
      this.toast.displayed = false
    }
  }

}

const socketInstance = new SocketConnection('/NikanHub')

export default socketInstance
